import MediaflowService from "@/services/MediaflowService";
import router from "@/router";
import _ from "lodash";

const normalizeMediaflow = (mediaflow) => {
  const normalized = _.cloneDeep(mediaflow || {});
  if (Array.isArray(normalized.flows)) {
    normalized.flows.forEach((flow) => {
      if (!Array.isArray(flow.media)) return;
      flow.media.forEach((media) => {
        if (media.volume === undefined || media.volume === null) media.volume = 50;
        if (typeof media.show === "undefined") media.show = false;
        if (typeof media.muted === "undefined") media.muted = false;
        if (typeof media.audioOffset === "undefined") media.audioOffset = 0;
      });
    });
  }
  return normalized;
};

const defaultState = () => ({
  mediaflows: [],
  mediaflow: {},
  currentMediaflowId: "",
  mediaflowLoaded: false,
  teamPermissionError: false,
  isLoading: false,
  isLoaded: false,
});

const state = defaultState();

const mutations = {
  SET_LOADING(state, isLoading) {
    state.isLoading = isLoading;
  },
  GET_MEDIAFLOWS(state, mediaflows) {
    state.mediaflows = mediaflows;
    state.isLoaded = true;
    state.isLoading = false;
  },
  GET_MEDIAFLOW(state, mediaflow) {
    state.mediaflow = mediaflow;
    state.mediaflowLoaded = true;
  },
  SET_CURRENT_MEDIAFLOW(state, id) {
    state.currentMediaflowId = id;
  },
  SET_MEDIAFLOW_SOURCE_STREAM(state, { index, stream }) {
      state.mediaflow.streams[index] = stream
  },
  REMOVE_MEDIAFLOW(state, id) {
    const objIndex = state.mediaflows.findIndex(obj => { 
      return obj._id === id
    })
    if (objIndex >= 0) {
      state.mediaflows.splice(objIndex, 1)
    }
  },
  TOGGLE_MEDIAFLOW(state, id) {
    const objIndex = state.mediaflows.findIndex(obj => { 
      return obj._id === id
    })

    if (objIndex >= 0) {
      Object.assign(state.mediaflows[objIndex], { enabled: !state.mediaflows[objIndex].enabled })
      state.mediaflow.enabled = !state.mediaflow.enabled
    }
  },
  SET_MEDIAFLOW_NAME(state, { id, name }) {
    const objIndex = state.mediaflows.findIndex(obj => { 
      return obj._id === id
    })

    if (objIndex >= 0) {
      Object.assign(state.mediaflows[objIndex], { name })
      state.mediaflow.name = name
    }
  },
  SET_MEDIAFLOW_DESTINATION(state, { id, destination }) {
    const objIndex = state.mediaflows.findIndex(obj => { 
      return obj._id === id
    })

    if (objIndex >= 0) {
      Object.assign(state.mediaflows[objIndex], { destination })
      state.mediaflow.destination = destination
    }
  },
  SET_MEDIAFLOW_RESOLUTION_PRESET(state, { id, resolutionPreset }) {
    const objIndex = state.mediaflows.findIndex(obj => { 
      return obj._id === id
    })

    if (objIndex >= 0) {
      Object.assign(state.mediaflows[objIndex], { resolutionPreset })
      state.mediaflow.resolutionPreset = resolutionPreset
    }
  },
  SET_MEDIAFLOW_SCREEN_RESOLUTION(state, { id, screenResolution }) {
    const objIndex = state.mediaflows.findIndex(obj => {
      return obj._id === id
    })

    if (objIndex >= 0) {
      Object.assign(state.mediaflows[objIndex], { screenResolution })
      state.mediaflow.screenResolution = screenResolution
    }
  },
  SET_MEDIAFLOW_BITRATE(state, { id, bitrate }) {
    const objIndex = state.mediaflows.findIndex(obj => { 
      return obj._id === id
    })

    if (objIndex >= 0) {
      Object.assign(state.mediaflows[objIndex], { bitrate })
      state.mediaflow.bitrate = bitrate
    }
  },
  SET_MEDIAFLOW_FPS(state, { id, fps }) {
    const objIndex = state.mediaflows.findIndex(obj => { 
      return obj._id === id
    })

    if (objIndex >= 0) {
      Object.assign(state.mediaflows[objIndex], { fps })
      state.mediaflow.fps = fps
    }
  },
  SET_MEDIAFLOW_TEAM_PERMISSION_ERROR(state, { perm }) {
    state.teamPermissionError = perm;
  },
  ADD_FLOW(state, { mediaflowId, flow }) {
    if (state.mediaflow._id === mediaflowId) {
      if (!state.mediaflow.flows) {
        state.mediaflow.flows = [];
      }
      state.mediaflow.flows.push(flow);
    }
    const objIndex = state.mediaflows.findIndex(obj => obj._id === mediaflowId);
    if (objIndex >= 0) {
      if (!state.mediaflows[objIndex].flows) {
        state.mediaflows[objIndex].flows = [];
      }
      state.mediaflows[objIndex].flows.push(flow);
    }
  },
  ADD_FLOW_MEDIA(state, { mediaflowId, flowId, media }) {
    if (state.mediaflow._id === mediaflowId) {
      const flowIndex = state.mediaflow.flows?.findIndex(flow => flow._id === flowId);
      if (flowIndex >= 0) {
        if (!state.mediaflow.flows[flowIndex].media) {
          state.mediaflow.flows[flowIndex].media = [];
        }
        state.mediaflow.flows[flowIndex].media.push(media);
      }
    }
  },
  UPDATE_FLOW_MEDIA(state, { mediaflowId, flowId, mediaId, media }) {
    if (state.mediaflow._id === mediaflowId) {
      const flow = state.mediaflow.flows?.find(f => f._id === flowId);
      if (flow && flow.media) {
        const mediaIndex = flow.media.findIndex(m => m.id === mediaId);
        if (mediaIndex >= 0) {
          Object.assign(flow.media[mediaIndex], media);
        }
      }
    }
    
    const mf = state.mediaflows.find(mf => mf._id === mediaflowId);
    if (mf) {
      const flow = mf.flows?.find(f => f._id === flowId);
      if (flow && flow.media) {
        const mediaIndex = flow.media.findIndex(m => m.id === mediaId);
        if (mediaIndex >= 0) {
          Object.assign(flow.media[mediaIndex], media);
        }
      }
    }
  },
  UPDATE_FLOW_MEDIA_VOLUME(state, { mediaflowId, flowId, mediaId, volume }) {
    if (state.mediaflow._id === mediaflowId) {
      const flow = state.mediaflow.flows?.find(f => f._id === flowId);
      if (flow && flow.media) {
        const media = flow.media.find(m => m.id === mediaId);
        if (media) {
          media.volume = volume;
        }
      }
    }
    
    const mf = state.mediaflows.find(mf => mf._id === mediaflowId);
    if (mf) {
      const flow = mf.flows?.find(f => f._id === flowId);
      if (flow && flow.media) {
        const media = flow.media.find(m => m.id === mediaId);
        if (media) {
          media.volume = volume;
        }
      }
    }
  },
  UPDATE_FLOW_MEDIA_MUTED(state, { mediaflowId, flowId, mediaId, muted }) {
    if (state.mediaflow._id === mediaflowId) {
      const flow = state.mediaflow.flows?.find(f => f._id === flowId);
      if (flow && flow.media) {
        const media = flow.media.find(m => m.id === mediaId);
        if (media) {
          media.muted = muted;
        }
      }
    }
    
    const mf = state.mediaflows.find(mf => mf._id === mediaflowId);
    if (mf) {
      const flow = mf.flows?.find(f => f._id === flowId);
      if (flow && flow.media) {
        const media = flow.media.find(m => m.id === mediaId);
        if (media) {
          media.muted = muted;
        }
      }
    }
  },
  UPDATE_FLOW_MEDIA_AUDIO_OFFSET(state, { mediaflowId, flowId, mediaId, audioOffset }) {
    if (state.mediaflow._id === mediaflowId) {
      const flow = state.mediaflow.flows?.find(f => f._id === flowId);
      if (flow && flow.media) {
        const media = flow.media.find(m => m.id === mediaId);
        if (media) {
          media.audioOffset = audioOffset;
        }
      }
    }
    
    const mf = state.mediaflows.find(mf => mf._id === mediaflowId);
    if (mf) {
      const flow = mf.flows?.find(f => f._id === flowId);
      if (flow && flow.media) {
        const media = flow.media.find(m => m.id === mediaId);
        if (media) {
          media.audioOffset = audioOffset;
        }
      }
    }
  },
  REORDER_FLOW_MEDIA(state, { mediaflowId, flowId, fromIndex, toIndex }) {
    if (state.mediaflow._id === mediaflowId) {
      const flow = state.mediaflow.flows?.find(f => f._id === flowId);
      if (flow && flow.media) {
        const mediaItem = flow.media.splice(fromIndex, 1)[0];
        flow.media.splice(toIndex, 0, mediaItem);
      }
    }
    
    const mf = state.mediaflows.find(mf => mf._id === mediaflowId);
    if (mf) {
      const flow = mf.flows?.find(f => f._id === flowId);
      if (flow && flow.media) {
        const mediaItem = flow.media.splice(fromIndex, 1)[0];
        flow.media.splice(toIndex, 0, mediaItem);
      }
    }
  },
  CLEAR_FLOW_MEDIA(state, { mediaflowId, flowId }) {
    if (state.mediaflow._id === mediaflowId) {
      const flowIndex = state.mediaflow.flows?.findIndex(flow => flow._id === flowId);
      if (flowIndex >= 0) {
        state.mediaflow.flows[flowIndex].media = [];
      }
    }
    const objIndex = state.mediaflows.findIndex(obj => obj._id === mediaflowId);
    if (objIndex >= 0) {
      const flowIndex = state.mediaflows[objIndex].flows?.findIndex(flow => flow._id === flowId);
      if (flowIndex >= 0) {
        state.mediaflows[objIndex].flows[flowIndex].media = [];
      }
    }
  },
  TOGGLE_FLOW_MEDIA_SHOW(state, { mediaflowId, flowId, mediaId }) {
    if (state.mediaflow._id === mediaflowId) {
      const flow = state.mediaflow.flows?.find(f => f._id === flowId);
      if (flow && flow.media) {
        const media = flow.media.find(m => m._id === mediaId);
        if (media) media.show = !media.show;
      }
    }
    const mf = state.mediaflows.find(mf => mf._id === mediaflowId);
    if (mf) {
      const flow = mf.flows?.find(f => f._id === flowId);
      if (flow && flow.media) {
        const media = flow.media.find(m => m._id === mediaId);
        if (media) media.show = !media.show;
      }
    }
  },
  DELETE_FLOW_MEDIA(state, { mediaflowId, flowId, mediaId }) {
    if (state.mediaflow._id === mediaflowId) {
      const flow = state.mediaflow.flows?.find(f => f._id === flowId);
      if (flow && flow.media) {
        const idx = flow.media.findIndex(m => m.id === mediaId);
        if (idx !== -1) flow.media.splice(idx, 1);
      }
    }
    const mf = state.mediaflows.find(mf => mf._id === mediaflowId);
    if (mf) {
      const flow = mf.flows?.find(f => f._id === flowId);
      if (flow && flow.media) {
        const idx = flow.media.findIndex(m => m.id === mediaId);
        if (idx !== -1) flow.media.splice(idx, 1);
      }
    }
  },
  REMOVE_FLOW(state, { mediaflowId, flowId }) {
    if (state.mediaflow._id === mediaflowId) {
      const flowIndex = state.mediaflow.flows?.findIndex(flow => flow._id === flowId);
      if (flowIndex >= 0) {
        state.mediaflow.flows.splice(flowIndex, 1);
      }
    }
    const mf = state.mediaflows.find(mf => mf._id === mediaflowId);
    if (mf && mf.flows) {
      const flowIndex = mf.flows.findIndex(flow => flow._id === flowId);
      if (flowIndex >= 0) {
        mf.flows.splice(flowIndex, 1);
      }
    }
  },
  SET_FLOW_NAME(state, { mediaflowId, flowId, name }) {
    if (state.mediaflow._id === mediaflowId) {
      const flow = state.mediaflow.flows?.find(f => f._id === flowId);
      if (flow) {
        flow.name = name;
      }
    }
    const mf = state.mediaflows.find(mf => mf._id === mediaflowId);
    if (mf && mf.flows) {
      const flow = mf.flows.find(f => f._id === flowId);
      if (flow) {
        flow.name = name;
      }
    }
  },
  SWITCH_FLOW(state, { mediaflowId, flowId }) {
    // Update current mediaflow
    if (state.mediaflow._id === mediaflowId && state.mediaflow.flows) {
      state.mediaflow.flows.forEach(flow => {
        flow.active = flow._id === flowId;
      });
    }
    // Update mediaflows array
    const mf = state.mediaflows.find(mf => mf._id === mediaflowId);
    if (mf && mf.flows) {
      mf.flows.forEach(flow => {
        flow.active = flow._id === flowId;
      });
    }
  },
  CLEAR_OBS_INSTANCE(state, mediaflowId) {
    if (state.mediaflow._id === mediaflowId) {
      state.mediaflow.obsInstance = null;
      state.mediaflow.flowInstanceReservedAt = null;
      state.mediaflow.flowInstanceReserveTimeSec = null;
    }
    
    const mf = state.mediaflows.find(mf => mf._id === mediaflowId);
    if (mf) {
      mf.obsInstance = null;
      mf.flowInstanceReservedAt = null;
      mf.flowInstanceReserveTimeSec = null;
    }
  },
  SET_OBS_INSTANCE(state, { mediaflowId, obsInstance, flowInstanceReservedAt, flowInstanceReserveTimeSec }) {
    if (state.mediaflow._id === mediaflowId) {
      state.mediaflow.obsInstance = obsInstance;
      state.mediaflow.flowInstanceReservedAt = flowInstanceReservedAt;
      state.mediaflow.flowInstanceReserveTimeSec = flowInstanceReserveTimeSec;
    }
    const mf = state.mediaflows.find(mf => mf._id === mediaflowId);
    if (mf) {
      mf.obsInstance = obsInstance;
      mf.flowInstanceReservedAt = flowInstanceReservedAt;
      mf.flowInstanceReserveTimeSec = flowInstanceReserveTimeSec;
    }
  },
};

const actions = {
  async getMediaflows({ commit, state }, forceRefresh = false) {
    if (state.isLoaded && !forceRefresh) {
      return state.mediaflows;
    }

    if (state.isLoading) {
      return new Promise((resolve) => {
        const checkLoading = setInterval(() => {
          if (!state.isLoading) {
            clearInterval(checkLoading);
            resolve(state.mediaflows);
          }
        }, 50);
      });
    }

    commit("SET_LOADING", true);
    try {
      const mediaflows = await MediaflowService.getMediaflows();
      commit("GET_MEDIAFLOWS", mediaflows);
      return mediaflows;
    } catch (error) {
      commit("SET_LOADING", false);
      throw error;
    }
  },
  seedMediaflow({ commit, state }, id) {
    const listed = state.mediaflows.find((mediaflow) => mediaflow._id === id);
    if (!listed) return false;
    commit("GET_MEDIAFLOW", normalizeMediaflow(listed));
    return true;
  },
  async fetchMediaflow(_context, id) {
    return normalizeMediaflow(await MediaflowService.getMediaflow(id));
  },
  applyMediaflow({ commit }, mediaflow) {
    commit("GET_MEDIAFLOW", normalizeMediaflow(mediaflow));
  },
  async getMediaflow({ dispatch }, id) {
    const mediaflow = await dispatch("fetchMediaflow", id);
    dispatch("applyMediaflow", mediaflow);
    return mediaflow;
  },
  setSource({ commit }, { index, stream }) {
    commit("SET_MEDIAFLOW_SOURCE_STREAM", { index, stream })
  },
  setCurrentMediaflow({ commit }, id) {
    commit("SET_CURRENT_MEDIAFLOW", id);
  },
  removeMediaflow({ commit }, { id }) {
    commit("REMOVE_MEDIAFLOW", id)
  },
  toggleMediaflow({ commit }, { id }) {
    commit("TOGGLE_MEDIAFLOW", id)
  },
  setLayout({ commit }, { id, streams, layout }) {
    commit("SET_MEDIAFLOW_LAYOUT", { id, streams, layout })
  },
  setName({ commit }, { id, name }) {
    commit("SET_MEDIAFLOW_NAME", { id, name })
  },
  setDestination({ commit }, { id, destination }) {
    commit("SET_MEDIAFLOW_DESTINATION", { id, destination })
  },
  setResolutionPreset({ commit }, { id, resolutionPreset }) {
    commit("SET_MEDIAFLOW_RESOLUTION_PRESET", { id, resolutionPreset })
  },
  setScreenResolution({ commit }, { id, screenResolution }) {
    commit("SET_MEDIAFLOW_SCREEN_RESOLUTION", { id, screenResolution })
  },
  setBitrate({ commit }, { id, bitrate }) {
    commit("SET_MEDIAFLOW_BITRATE", { id, bitrate })
  },
  setFps({ commit }, { id, fps }) {
    commit("SET_MEDIAFLOW_FPS", { id, fps })
  },
  setTeamPermissionError({ commit }, { error }) {
    commit("SET_MEDIAFLOW_TEAM_PERMISSION_ERROR", { perm: error })
  },
  addFlow({ commit }, { mediaflowId, flow }) {
    commit("ADD_FLOW", { mediaflowId, flow })
  },
  addFlowMedia({ commit }, { mediaflowId, flowId, media }) {
    commit("ADD_FLOW_MEDIA", { mediaflowId, flowId, media })
  },
  updateFlowMedia({ commit }, { mediaflowId, flowId, mediaId, media }) {
    commit("UPDATE_FLOW_MEDIA", { mediaflowId, flowId, mediaId, media })
  },
  updateFlowMediaVolume({ commit }, { mediaflowId, flowId, mediaId, volume }) {
    commit("UPDATE_FLOW_MEDIA_VOLUME", { mediaflowId, flowId, mediaId, volume })
  },
  updateFlowMediaMuted({ commit }, { mediaflowId, flowId, mediaId, muted }) {
    commit("UPDATE_FLOW_MEDIA_MUTED", { mediaflowId, flowId, mediaId, muted })
  },
  updateFlowMediaAudioOffset({ commit }, { mediaflowId, flowId, mediaId, audioOffset }) {
    commit("UPDATE_FLOW_MEDIA_AUDIO_OFFSET", { mediaflowId, flowId, mediaId, audioOffset })
  },
  reorderFlowMedia({ commit }, { mediaflowId, flowId, fromIndex, toIndex }) {
    commit("REORDER_FLOW_MEDIA", { mediaflowId, flowId, fromIndex, toIndex })
  },
  clearFlowMedia({ commit }, { mediaflowId, flowId }) {
    commit("CLEAR_FLOW_MEDIA", { mediaflowId, flowId })
  },
  async toggleFlowMediaShow({ commit }, { mediaflowId, flowId, mediaId }) {
    await MediaflowService.toggleFlowMedia(mediaflowId, flowId, mediaId);
    commit('TOGGLE_FLOW_MEDIA_SHOW', { mediaflowId, flowId, mediaId });
  },
  async deleteFlowMedia({ commit }, { mediaflowId, flowId, mediaId }) {
    const res = await MediaflowService.deleteFlowMedia(mediaflowId, flowId, mediaId);
    if (res && res.success !== false) {
      commit('DELETE_FLOW_MEDIA', { mediaflowId, flowId, mediaId });
    }
    return res;
  },
  async removeFlow({ commit }, { mediaflowId, flowId }) {
    const res = await MediaflowService.deleteFlow(mediaflowId, flowId);
    if (res && res.success !== false) {
      commit('REMOVE_FLOW', { mediaflowId, flowId });
    }
    return res;
  },
  setFlowName({ commit }, { mediaflowId, flowId, name }) {
    commit('SET_FLOW_NAME', { mediaflowId, flowId, name });
  },
  async switchFlow({ commit }, { mediaflowId, flowId }) {
    const res = await MediaflowService.switchScene(mediaflowId, flowId);
    if (res && res.success !== false) {
      commit('SWITCH_FLOW', { mediaflowId, flowId });
    }
    return res;
  },
  clearObsInstance({ commit }, mediaflowId) {
    commit('CLEAR_OBS_INSTANCE', mediaflowId);
  },
  setObsInstance({ commit }, { mediaflowId, obsInstance, flowInstanceReservedAt, flowInstanceReserveTimeSec }) {
    commit('SET_OBS_INSTANCE', { mediaflowId, obsInstance, flowInstanceReservedAt, flowInstanceReserveTimeSec });
  },
};

const getters = {
  mediaflows: (state) => state.mediaflows,
  mediaflow: (state) => state.mediaflow,
  currentMediaflow: (state) => {
    if (state.currentMediaflowId === '') {
      return state.mediaflows.find(
        (mf) =>
          mf._id === (router.currentRoute.params && router.currentRoute.params.id)
      );
    } else {
      return state.mediaflows.find((mf) => mf._id === state.currentMediaflowId);
    }
  },
  mediaflowLoaded: (state) => state.mediaflowLoaded,
  teamPermissionError: (state) => state.teamPermissionError,
  isLoaded: (state) => state.isLoaded,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}; 